import React, { useContext, useState } from 'react';
import { Badge, Box, Button, Divider, Flex, Grid, Spinner, Text, useDisclosure } from '@chakra-ui/react';
import { useMutation, useQuery } from '@apollo/client';
import moment from 'moment';
import { get } from 'lodash';

import {
  AccessPoint,
  AmazonHubCounterProfile,
  AmazonHubCounterProfileQuery,
  GET_AMAZON_HUB_COUNTER_PROFILE,
  GET_HUB_COUNTER,
  HubCounter,
  HubCounterStateQuery,
  REFRESH_AMAZON_HUB_SESSION,
  SUBMIT_HUB_COUNTER_TO_AMAZON,
  TOGGLE_HUB_COUNTER_ACTIVE,
  UPDATE_HUB_COUNTER,
  UPDATE_HUB_COUNTER_STATUS,
} from '../query';
import { useCustomToast } from '../../../hooks/useCustomToast';
import { useSpinner } from '../../../contexts/SpinnerContext';
import { resolveHubCounterDiagnostics } from './hubCounterState';
import { HubCounterLifecycleCtas } from './HubCounterLifecycleCtas';
import ConfirmHubCounterModal from '../modals/ConfirmHubCounter';
import RejectHubCounterModal from '../modals/RejectHubCounter';
import EnsurePartnerAccessPointsModal from '../modals/EnsurePartnerAccessPoints';
import SendHubCounterWaitlistEmailModal from '../modals/SendHubCounterWaitlistEmail';
import HubCounterDeveloperUtilities, {
  HubCounterActionConfirmDialog,
  HubCounterConfirmAction,
  HubCounterEndorActionPayload,
} from '../drawers/HubCounterDeveloperUtilities';
import { statusColorScheme } from '../../AccessPoints/List/Card';
import UserContext from '../../../contexts/UserContext';

const InfoRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Flex alignItems="flex-start" gap="2" mb="1">
    <Text fontSize="sm" color="gray.500" minW="180px" flexShrink={0}>
      {label}
    </Text>
    <Box fontSize="sm" fontWeight="medium" flex="1">
      {value ?? (
        <Text as="span" color="gray.400">
          —
        </Text>
      )}
    </Box>
  </Flex>
);

const SectionHeading = ({ children, mt = '4' }: { children: React.ReactNode; mt?: string }) => (
  <Text mt={mt} mb="2" fontSize="xs" fontWeight="semibold" color="gray.600" textTransform="uppercase" letterSpacing="wide">
    {children}
  </Text>
);

interface HubCounterPanelProps {
  hubCounter?: HubCounter | null;
  accessPoint?: AccessPoint | null;
  dropPointId: string;
  refetch: () => Promise<any> | void;
  embedded?: boolean;
}

export const shouldShowEnsurePartnerAccessPoints = ({
  accessPoint,
  hubCounter,
}: {
  accessPoint?: AccessPoint | null;
  hubCounter?: HubCounter | null;
}) => !accessPoint || !hubCounter;

export const getEnsurePartnerAccessPointsLabel = ({
  accessPoint,
  hubCounter,
}: {
  accessPoint?: AccessPoint | null;
  hubCounter?: HubCounter | null;
}) => {
  if (!accessPoint && !hubCounter) {
    return 'UPS AccessPoint & Hub Counter anlegen';
  }

  if (!accessPoint) {
    return 'UPS AccessPoint anlegen';
  }

  return 'Hub Counter erstellen';
};

const formatDateTime = (value?: string | null) => (value ? moment(value).format('DD.MM.YYYY HH:mm') : undefined);

const getOperationalStateLabel = (hubCounter?: HubCounter | null) => {
  if (!hubCounter) {
    return 'Nicht angelegt';
  }

  return hubCounter.isActive ? 'Aktiv' : 'Inaktiv';
};

const formatSupportAddress = (profile?: AmazonHubCounterProfile | null) => {
  if (!profile) {
    return undefined;
  }

  const street = profile.addressFieldOne?.trim();
  const locality = [profile.postalCode?.trim(), profile.city?.trim()].filter(Boolean).join(' ');

  return [street, locality].filter(Boolean).join(', ') || undefined;
};

const formatCapabilities = (capabilities?: string[] | null) => {
  if (!capabilities?.length) {
    return undefined;
  }

  return capabilities.join(', ');
};

const HubCounterPanel = ({ hubCounter, accessPoint, dropPointId, refetch, embedded = false }: HubCounterPanelProps) => {
  const { user: internalUser } = useContext(UserContext);
  const { toggleSpinner } = useSpinner();
  const toast = useCustomToast();
  const confirmDisclosure = useDisclosure();
  const rejectDisclosure = useDisclosure();
  const ensureDisclosure = useDisclosure();
  const waitlistEmailDisclosure = useDisclosure();
  const developerUtilitiesDisclosure = useDisclosure();
  const [confirmAction, setConfirmAction] = useState<HubCounterConfirmAction | null>(null);
  const { data: stateData, refetch: refetchState } = useQuery<HubCounterStateQuery>(GET_HUB_COUNTER, {
    variables: { dropPointId },
    skip: !hubCounter,
    fetchPolicy: 'cache-and-network',
  });
  const [updateCounter, { loading: updateLoading }] = useMutation(UPDATE_HUB_COUNTER);
  const [submitCreateFeed, { loading: createLoading }] = useMutation(SUBMIT_HUB_COUNTER_TO_AMAZON);
  const [toggleHubCounterActive, { loading: deactivateLoading }] = useMutation(TOGGLE_HUB_COUNTER_ACTIVE);
  const [refreshSession, { loading: refreshLoading }] = useMutation(REFRESH_AMAZON_HUB_SESSION);
  const [updateHubCounterStatus, { loading: statusLoading }] = useMutation(UPDATE_HUB_COUNTER_STATUS);
  const stateSnapshot = stateData?.hubCounter;
  const resolvedHubCounter = stateSnapshot?.hubCounter ?? hubCounter ?? null;
  const shouldFetchSupportProfile = Boolean(resolvedHubCounter);
  const { data: supportProfileData } = useQuery<AmazonHubCounterProfileQuery>(GET_AMAZON_HUB_COUNTER_PROFILE, {
    variables: { dropPointId },
    skip: !shouldFetchSupportProfile,
    fetchPolicy: 'cache-and-network',
  });
  const diagnostics = resolvedHubCounter
    ? resolveHubCounterDiagnostics({
        lifecycle: resolvedHubCounter.status,
        displayState: stateSnapshot?.displayState,
        lastFeedStatus: resolvedHubCounter.lastFeedStatus,
        createFeedCompleted: stateSnapshot?.createFeedCompleted,
        sessionStatus: stateSnapshot?.sessionStatus,
      })
    : null;
  const showEnsurePartnerAccessPoints = shouldShowEnsurePartnerAccessPoints({ accessPoint, hubCounter });
  const ensurePartnerAccessPointsLabel = getEnsurePartnerAccessPointsLabel({ accessPoint, hubCounter });
  // When embedded, the parent AccessPoint tab owns the warning banner and the UPS-related CTA;
  // this panel only keeps the CTA when solely the Amazon Hub Counter is missing.
  const showEnsureCta = embedded ? Boolean(accessPoint) && !hubCounter : showEnsurePartnerAccessPoints;
  const showEnsureWarning = !embedded && showEnsurePartnerAccessPoints;
  const supportProfile = supportProfileData?.amazonHubCounterProfile;

  const refetchAll = async () => {
    await Promise.all([Promise.resolve(refetch()), resolvedHubCounter ? Promise.resolve(refetchState()) : Promise.resolve()]);
  };

  const handleStatusTransition = async (status: 'PENDING' | 'WAITLIST', comment: string) => {
    try {
      toggleSpinner(true);
      await updateHubCounterStatus({
        variables: {
          dropPointId,
          status,
          reason: comment,
          internalUserId: internalUser.id,
        },
      });
      await refetchAll();
      toast({
        title: 'Erfolg!',
        description:
          status === 'PENDING' ? 'Hub Counter Status wurde auf PENDING gesetzt.' : 'Hub Counter Status wurde auf WAITLIST gesetzt.',
        status: 'success',
      });
    } catch (error) {
      toast({ description: get(error, 'graphQLErrors.0.message', 'Fehler beim Ändern des Status.'), status: 'error' });
      throw error;
    } finally {
      toggleSpinner(false);
    }
  };

  const handleUpdate = async ({ immediate, comment }: HubCounterEndorActionPayload) => {
    try {
      toggleSpinner(true);
      await updateCounter({
        variables: { dropPointId, immediate, comment, internalUserId: internalUser.id },
      });
      await refetchAll();
      toast({
        title: 'Erfolg!',
        description: immediate ? 'Hub Counter wurde sofort bei Amazon aktualisiert.' : 'Hub Counter wurde bei Amazon aktualisiert.',
        status: 'success',
      });
    } catch (error) {
      toast({ description: get(error, 'graphQLErrors.0.message', 'Fehler beim Aktualisieren.'), status: 'error' });
      throw error;
    } finally {
      toggleSpinner(false);
    }
  };

  const handleCreateFeed = async ({ immediate, comment }: HubCounterEndorActionPayload) => {
    try {
      toggleSpinner(true);
      await submitCreateFeed({
        variables: { dropPointId, immediate, comment, internalUserId: internalUser.id },
      });
      await refetchAll();
      toast({
        title: 'Erfolg!',
        description: immediate ? 'CREATE-Feed wurde sofort an Amazon gesendet.' : 'CREATE-Feed wurde in die Amazon-Queue gestellt.',
        status: 'success',
      });
    } catch (error) {
      toast({ description: get(error, 'graphQLErrors.0.message', 'Fehler beim Senden des CREATE-Feeds.'), status: 'error' });
      throw error;
    } finally {
      toggleSpinner(false);
    }
  };

  const handleDeactivate = async ({ immediate, comment }: HubCounterEndorActionPayload) => {
    try {
      toggleSpinner(true);
      await toggleHubCounterActive({
        variables: { dropPointId, active: false, immediate, comment, internalUserId: internalUser.id },
      });
      await refetchAll();
      toast({
        title: 'Erfolg!',
        description: immediate
          ? 'Hub Counter ist lokal inaktiv; Deaktivierungs-Feed wurde sofort an Amazon gesendet.'
          : 'Hub Counter ist lokal inaktiv; Deaktivierungs-Feed wurde in die Amazon-Queue gestellt.',
        status: 'success',
      });
    } catch (error) {
      toast({ description: get(error, 'graphQLErrors.0.message', 'Fehler beim Deaktivieren.'), status: 'error' });
      throw error;
    } finally {
      toggleSpinner(false);
    }
  };

  const handleRefreshSession = async ({ comment }: HubCounterEndorActionPayload) => {
    try {
      toggleSpinner(true);
      await refreshSession({ variables: { dropPointId, comment, internalUserId: internalUser.id } });
      await refetchAll();
      toast({ title: 'Erfolg!', description: 'Amazon Hub Session wurde aktualisiert.', status: 'success' });
    } catch (error) {
      toast({ description: get(error, 'graphQLErrors.0.message', 'Fehler beim Aktualisieren der Session.'), status: 'error' });
      throw error;
    } finally {
      toggleSpinner(false);
    }
  };

  const handleConfirmAction = async (payload: HubCounterEndorActionPayload) => {
    if (confirmAction === 'create') {
      await handleCreateFeed(payload);
    } else if (confirmAction === 'update') {
      await handleUpdate(payload);
    } else if (confirmAction === 'deactivate') {
      await handleDeactivate(payload);
    } else if (confirmAction === 'refresh') {
      await handleRefreshSession(payload);
    } else if (confirmAction === 'pending') {
      await handleStatusTransition('PENDING', payload.comment);
    } else if (confirmAction === 'waitlist') {
      await handleStatusTransition('WAITLIST', payload.comment);
    }
    setConfirmAction(null);
  };

  const confirmLoading =
    confirmAction === 'create'
      ? createLoading
      : confirmAction === 'update'
        ? updateLoading
        : confirmAction === 'deactivate'
          ? deactivateLoading
          : confirmAction === 'refresh'
            ? refreshLoading
            : statusLoading;

  const lifecycleCtas =
    diagnostics && resolvedHubCounter ? (
      <HubCounterLifecycleCtas
        diagnostics={diagnostics}
        onMoveToPending={() => setConfirmAction('pending')}
        onMoveToWaitlist={() => setConfirmAction('waitlist')}
        onReject={rejectDisclosure.onOpen}
        onConfirm={confirmDisclosure.onOpen}
        statusLoading={statusLoading}
      />
    ) : null;

  return (
    <Box>
      <Box px={embedded ? '0' : '4'} pt={embedded ? '0' : '6'} pb={embedded ? '4' : '0'}>
        <Flex alignItems="center" justifyContent="space-between" gap="3" mb="4" flexWrap="wrap">
          <Box>
            <Text fontWeight="semibold" fontSize="sm">
              Amazon Support
            </Text>
          </Box>
          <Flex alignItems="center" gap="2" flexWrap="wrap">
            {lifecycleCtas}
            {diagnostics?.canSendWaitlistEmail ? (
              <Button size="sm" colorScheme="brand" variant="outline" onClick={waitlistEmailDisclosure.onOpen}>
                218 Email senden
              </Button>
            ) : null}
            {showEnsureCta ? (
              <Button size="sm" colorScheme="brand" variant="outline" onClick={ensureDisclosure.onOpen}>
                {ensurePartnerAccessPointsLabel}
              </Button>
            ) : null}
            {diagnostics && resolvedHubCounter ? (
              <Button size="sm" variant="outline" colorScheme="gray" onClick={developerUtilitiesDisclosure.onOpen}>
                Developer Utilities
              </Button>
            ) : null}
            {updateLoading || refreshLoading || statusLoading ? <Spinner size="sm" /> : null}
          </Flex>
        </Flex>
      </Box>

      {showEnsureWarning ? (
        <Box mb="4" p="3" bg="orange.50" borderRadius="md">
          <Text fontSize="xs" fontWeight="semibold" color="orange.700" mb="1">
            Partner-Datensätze unvollständig
          </Text>
          <Text fontSize="sm" color="orange.800">
            Für diesen DropPoint fehlt mindestens einer der benötigten UPS- oder Amazon-Einträge. Über &quot;
            {ensurePartnerAccessPointsLabel}&quot; werden nur die fehlenden Datensätze ergänzt.
          </Text>
        </Box>
      ) : null}

      {embedded ? (
        <Box>
          {resolvedHubCounter ? (
            <>
              <SectionHeading mt="0">Status</SectionHeading>
              <InfoRow
                label="Hub Counter Status"
                value={<Badge colorScheme={statusColorScheme[resolvedHubCounter.status] ?? 'gray'}>{resolvedHubCounter.status}</Badge>}
              />
              <InfoRow label="Betriebsstatus" value={getOperationalStateLabel(resolvedHubCounter)} />

              <SectionHeading>Identifiers</SectionHeading>
              <InfoRow label="Hub Counter ID" value={supportProfile?.hubCounterID ?? resolvedHubCounter.hubCounterID} />
              <InfoRow
                label="Amazon Access Point ID"
                value={supportProfile?.amazonAccessPointId ?? resolvedHubCounter.amazonAccessPointId}
              />

              <SectionHeading>Name and Login Claim</SectionHeading>
              <InfoRow label="Name" value={supportProfile?.name ?? resolvedHubCounter.name} />
              <InfoRow label="Login Claim" value={supportProfile?.loginClaim ?? resolvedHubCounter.loginClaim} />

              <SectionHeading>Address</SectionHeading>
              <InfoRow label="Address" value={formatSupportAddress(supportProfile)} />

              <SectionHeading>Communication</SectionHeading>
              <InfoRow label="Email" value={supportProfile?.communicationEmail ?? resolvedHubCounter.communicationEmail} />
              <InfoRow label="Phone" value={supportProfile?.communicationPhone ?? resolvedHubCounter.communicationPhone} />

              <SectionHeading>Capacity threshold</SectionHeading>
              <InfoRow label="Capacity threshold" value={supportProfile?.capacityThreshold ?? resolvedHubCounter.capacityThreshold} />

              <SectionHeading>Capabilities</SectionHeading>
              <InfoRow label="Capabilities" value={formatCapabilities(supportProfile?.capabilities ?? resolvedHubCounter.capabilities)} />
            </>
          ) : (
            <Text fontSize="sm" color="gray.500">
              Für diesen DropPoint ist noch kein Amazon Hub Counter angelegt.
            </Text>
          )}
        </Box>
      ) : (
        <Grid templateColumns={['1fr', '1fr 1fr']} gridGap="8" px="4" py="8">
          <Box>
            {resolvedHubCounter ? (
              <>
                <InfoRow
                  label="Amazon Lifecycle"
                  value={
                    diagnostics ? (
                      <Badge colorScheme={diagnostics.lifecycle.colorScheme}>{diagnostics.lifecycle.value}</Badge>
                    ) : (
                      resolvedHubCounter.status
                    )
                  }
                />
                <InfoRow label="Betriebsstatus" value={getOperationalStateLabel(resolvedHubCounter)} />
                <InfoRow label="Hub Counter ID" value={resolvedHubCounter.hubCounterID} />
                <InfoRow label="Amazon Access Point ID" value={resolvedHubCounter.amazonAccessPointId} />
                <InfoRow label="Name" value={resolvedHubCounter.name} />
              </>
            ) : (
              <Text fontSize="sm" color="gray.500">
                Für diesen DropPoint ist noch kein Amazon Hub Counter angelegt.
              </Text>
            )}
          </Box>

          <Box>
            {resolvedHubCounter ? (
              <>
                <InfoRow label="Amazon verifiziert" value={resolvedHubCounter.amazonVerified ? 'Ja' : 'Nein'} />
                <InfoRow label="Verifiziert am" value={formatDateTime(resolvedHubCounter.amazonVerifiedAt)} />
                <InfoRow label="Erstellt am" value={formatDateTime(resolvedHubCounter.createdAt)} />
                <InfoRow label="Zuletzt aktualisiert" value={formatDateTime(resolvedHubCounter.updatedAt)} />
                <InfoRow label="Letzte Deaktivierung" value={formatDateTime(resolvedHubCounter.lastDeactivationTimestamp)} />
                <InfoRow label="Deaktiviert bis" value={formatDateTime(resolvedHubCounter.deactivatedUntil)} />
              </>
            ) : (
              <Text fontSize="sm" color="gray.500">
                Weitere Amazon-Supportdetails werden sichtbar, sobald ein Hub Counter vorhanden ist.
              </Text>
            )}
          </Box>
        </Grid>
      )}

      {resolvedHubCounter?.rejectionReason ? (
        <Box pb={embedded ? '0' : '8'} pt={embedded ? '4' : '0'} px={embedded ? '0' : '4'}>
          {embedded ? null : <Divider mb="4" />}
          <Box p="3" bg="red.50" borderRadius="md">
            <Text fontSize="xs" fontWeight="semibold" color="red.600" mb="1">
              Ablehnung
            </Text>
            <Text fontSize="sm" color="red.700">
              {resolvedHubCounter.rejectionReason}
            </Text>
            {resolvedHubCounter.rejectionTimestamp ? (
              <Text fontSize="xs" color="red.400" mt="1">
                {moment(resolvedHubCounter.rejectionTimestamp).format('DD.MM.YYYY HH:mm')}
              </Text>
            ) : null}
          </Box>
        </Box>
      ) : null}

      {resolvedHubCounter ? (
        <>
          <ConfirmHubCounterModal
            isOpen={confirmDisclosure.isOpen}
            onClose={confirmDisclosure.onClose}
            dropPointId={dropPointId}
            initialHubCounterId={resolvedHubCounter.hubCounterID}
            refetch={refetch}
            refetchState={refetchState}
          />
          <RejectHubCounterModal
            isOpen={rejectDisclosure.isOpen}
            onClose={rejectDisclosure.onClose}
            dropPointId={dropPointId}
            refetch={refetch}
            refetchState={refetchState}
          />
        </>
      ) : null}

      <EnsurePartnerAccessPointsModal
        isOpen={ensureDisclosure.isOpen}
        onClose={ensureDisclosure.onClose}
        dropPointId={dropPointId}
        refetch={refetch}
        refetchState={resolvedHubCounter ? refetchState : undefined}
      />

      <SendHubCounterWaitlistEmailModal
        isOpen={waitlistEmailDisclosure.isOpen}
        onClose={waitlistEmailDisclosure.onClose}
        dropPointId={dropPointId}
        refetch={refetch}
        refetchState={resolvedHubCounter ? refetchState : undefined}
      />

      {resolvedHubCounter && diagnostics ? (
        <HubCounterDeveloperUtilities
          isOpen={developerUtilitiesDisclosure.isOpen}
          onClose={developerUtilitiesDisclosure.onClose}
          dropPointId={dropPointId}
          hubCounter={resolvedHubCounter}
          diagnostics={diagnostics}
          stateSnapshot={stateSnapshot}
          lifecycleCtas={lifecycleCtas}
          onRequestAction={setConfirmAction}
          updateLoading={updateLoading}
          createLoading={createLoading}
          deactivateLoading={deactivateLoading}
          refreshLoading={refreshLoading}
        />
      ) : null}
      <HubCounterActionConfirmDialog
        action={confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={handleConfirmAction}
        isLoading={confirmLoading}
      />
    </Box>
  );
};

export default HubCounterPanel;
